import Swal from "sweetalert2";

/**
 * Total de contenedores de la proforma (misma heurística que CollectionSummary / balance).
 */
export function resolveProformContainerCountFromRecord(proformDoc, balanceProformDoc) {
  const p = proformDoc || {};
  const b = balanceProformDoc || {};
  const raw =
    p.qtycontainers != null && p.qtycontainers !== ""
      ? p.qtycontainers
      : b.qtycontainers != null && b.qtycontainers !== ""
        ? b.qtycontainers
        : p.containers != null && p.containers !== ""
          ? p.containers
          : b.containers != null && b.containers !== ""
            ? b.containers
            : "";
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Parsea `shipping.containers` tipo "1, 2, 3" → enteros válidos 1..maxTotal */
export function parseContainersCsv(raw, maxTotal) {
  if (raw == null || maxTotal <= 0) return [];
  const s = String(raw).trim();
  if (!s) return [];
  const out = [];
  const seen = new Set();
  for (const part of s.split(",")) {
    const n = parseInt(String(part).trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > maxTotal) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/**
 * Slots 1..maxTotal ya cubiertos por embarques existentes.
 * Si un embarque tiene `containers` explícito, se usa; si no, se rellenan los `qtycontainers`
 * menores libres (compatibilidad con datos viejos).
 */
export function collectUsedContainerIndices(shippingRows, maxTotal) {
  const used = new Set();
  const rows = Array.isArray(shippingRows) ? shippingRows : [];
  if (!(maxTotal > 0)) return used;

  for (const row of rows) {
    if (!row) continue;
    const parsed = parseContainersCsv(row.containers, maxTotal);
    if (parsed.length > 0) {
      parsed.forEach((n) => used.add(n));
      continue;
    }
    const q = Math.round(Number(row.qtycontainers));
    if (!Number.isFinite(q) || q <= 0) continue;
    let added = 0;
    for (let i = 1; i <= maxTotal && added < q; i++) {
      if (!used.has(i)) {
        used.add(i);
        added++;
      }
    }
  }
  return used;
}

export function availableContainerSlots(maxTotal, usedSet) {
  const a = [];
  for (let i = 1; i <= maxTotal; i++) {
    if (!usedSet.has(i)) a.push(i);
  }
  return a;
}

/**
 * Alinea `qtycontainers` y, si hay un solo bloque productandprice, su `qty_containers` al total elegido.
 */
export function patchShippingItemQtyToMatchContainerPick(item, pickedCount) {
  if (!item || !Number.isFinite(pickedCount) || pickedCount < 1) return;
  item.qtycontainers = pickedCount;
  const raw = item.productandprice;
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
  if (arr.length === 1 && arr[0] && typeof arr[0] === "object") {
    const line = { ...arr[0] };
    line.qty_containers = pickedCount;
    item.productandprice = Array.isArray(raw) ? [line] : line;
  }
}

/**
 * Diálogo: elegir qué contenedores (1..N) asigna este embarque.
 * @param {function(string, object=): string} t - i18n $t
 * @returns {Promise<number[]|null>} null si cancela / sin cupo; array ordenado si confirma
 */
export async function showShippingContainerPickDialog(t, totalContainerCount, shippingRows) {
  const total = Math.round(Number(totalContainerCount));
  if (!(total > 0)) return [];

  const used = collectUsedContainerIndices(shippingRows, total);
  const avail = availableContainerSlots(total, used);

  if (avail.length === 0) {
    await Swal.fire({
      icon: "warning",
      title: t("financialSummary.pickShippingContainersNoneLeftTitle"),
      text: t("financialSummary.pickShippingContainersNoneLeft"),
    });
    return null;
  }

  const checks = avail
    .map(
      (n) =>
        `<label class="form-check form-check-inline mb-1 me-2"><input type="checkbox" class="form-check-input sc-slot-cb" value="${n}"> <span class="form-check-label">#${n}</span></label>`
    )
    .join("");

  const { value: picked, isDismissed } = await Swal.fire({
    title: t("financialSummary.pickShippingContainersTitle"),
    html: `<p class="text-start small text-muted mb-2">${t("financialSummary.pickShippingContainersHint", { total })}</p><div class="text-start d-flex flex-wrap align-items-center">${checks}</div>`,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: t("financialSummary.pickShippingContainersConfirm"),
    cancelButtonText: t("financialSummary.pickShippingContainersCancel"),
    preConfirm: () => {
      const popup = Swal.getPopup();
      if (!popup) return false;
      const els = popup.querySelectorAll(".sc-slot-cb:checked");
      const nums = Array.from(els)
        .map((e) => parseInt(String(e.value), 10))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b);
      if (nums.length < 1) {
        Swal.showValidationMessage(t("financialSummary.pickShippingContainersNeedOne"));
        return false;
      }
      return nums;
    },
  });

  if (isDismissed || picked == null) return null;
  return Array.isArray(picked) ? picked : null;
}
